import {HIDE_TIP_TAPS_COUNT, MANY_MINING_TAPS_COUNT, MINING_TAPS_COUNT} from '../ClickerGame.constants';
import {ClickerGCSEngine} from '../ClickerGame.types';
import {waveConfig} from '../components/WaveEmitterComponent/WaveEmitterComponent.config';
import {BackgroundGalaxy} from '../game-objects/BackgroundGalaxy/BackgroundGalaxy';
import {Spaceship} from '../game-objects/Spaceship/Spaceship';
import {DI_TOKENS} from '@/di/di.tokens';
import {
  MainOnboardingFinishSteps,
  MainOnboardingStartSteps,
  MainOnboardingSteps,
  TooltipId,
} from '@/stores/OnboardingStore/OnboardingStore.constants';
import {Container, ContainerChild, Point, Sprite} from 'pixi.js';

import {injected} from 'brandi';

import {IGameScript} from '@specials/gcs-engine';
import {ParticleEmitter} from '@specials/particle-emitter';

import {TOOLTIP_VISIBLE_TIME} from '@/constants';
import {OnboardingService} from '@/services/shared/OnboardingService/OnboardingService';
import {OnboardingStore} from '@/stores/OnboardingStore/OnboardingStore';
import {CallbackCollector} from '@/utils/CallbackCollector';

import {ClickerGameAssetsLoader} from '../ClickerGameAssetsLoader';
import {ClickerGameEventNames, ClickerGameEvents} from '../ClickerGameEvents';

export class OnboardingScript implements IGameScript {
  private emitter: ParticleEmitter;
  private spaceship?: Spaceship;
  private galaxy?: BackgroundGalaxy;
  private tapsCounter: number;

  constructor(
    private readonly gcs: ClickerGCSEngine,
    private readonly assetsLoader: ClickerGameAssetsLoader,
    private readonly onboardingStore: OnboardingStore,
    private readonly onboardingService: OnboardingService,
    private readonly callbacksCollector: CallbackCollector,
    private readonly clickerEvents: ClickerGameEvents,
  ) {}

  public start(): void {
    this.tapsCounter = 0;

    this.spaceship = this.gcs.world.getGameObject(Spaceship);
    this.galaxy = this.gcs.world.getGameObject(BackgroundGalaxy);

    if (this.galaxy) {
      this.emitter = this.createEmitter(this.galaxy.viewComponent.view);
      const width = this.gcs.renderer.renderer.getFrameDimensions().width;
      this.emitter.config.spawnPosition = new Point(width / 2, 300);
    }

    this.callbacksCollector.add(this.onboardingStore.subscribe(this.handleOnboardingStateChanged));
    this.callbacksCollector.add(this.clickerEvents.subscribeOnEvent(ClickerGameEventNames.Tap, this.handleTap));

    // инициализация, если онбординг не с 1 шага
    this.handleOnboardingStateChanged();
  }

  public destroy(): void {
    this.callbacksCollector.execute();
  }

  private handleTap = (): void => {
    const onboardingStep = this.onboardingStore.getOnboardingStep();

    if (this.onboardingService.isMainOnboardingCompleted()) {
      this.tapsCounter++;

      if (this.tapsCounter === MANY_MINING_TAPS_COUNT) {
        if (!this.onboardingService.isOpenDailyGameTooltipViewed()) {
          this.onboardingService.showOpenDailyGameTooltip();
        } else if (!this.onboardingService.isSettingsTooltipViewed()) {
          this.onboardingService.showSettingsTooltip();
        }

        this.tapsCounter = 0;
      }
    }

    if (onboardingStep === MainOnboardingSteps.Taps) {
      this.tapsCounter++;

      if (this.tapsCounter === 25) {
        this.hideTapsTip();
        this.tapsCounter = 0;
      }
    }

    if (onboardingStep === MainOnboardingSteps.CrystalCounter) {
      this.tapsCounter++;

      if (this.tapsCounter === 25) {
        this.hideTapsTip();
        this.tapsCounter = 0;
      }
    }

    if (onboardingStep === MainOnboardingSteps.TapsMining || onboardingStep === MainOnboardingSteps.ContinueTap) {
      this.tapsCounter++;

      if (this.tapsCounter === MINING_TAPS_COUNT) {
        if (onboardingStep === MainOnboardingSteps.ContinueTap) {
          this.onboardingService.nextMainOnboardingStep(2);
        } else {
          this.onboardingService.nextMainOnboardingStep();
        }
        this.tapsCounter = 0;
      }
    }

    if (onboardingStep === MainOnboardingFinishSteps.FinishWithoutGift) {
      this.tapsCounter++;

      if (this.tapsCounter === HIDE_TIP_TAPS_COUNT) {
        this.onboardingService.nextMainOnboardingStep();
      }
    }
  };

  private handleOnboardingStateChanged = (): void => {
    if (!this.spaceship) return;

    const onboardingStep = this.onboardingStore.getOnboardingStep();

    if (
      onboardingStep === MainOnboardingStartSteps.StartCommon ||
      onboardingStep === MainOnboardingStartSteps.StartWithGift
    ) {
      this.spaceship.viewComponent.view.alpha = 0;
    }

    if (onboardingStep === MainOnboardingSteps.Taps) {
      this.spaceship.viewComponent.view.alpha = 1;
    }

    if (!onboardingStep && this.spaceship) {
      this.spaceship.viewComponent.view.alpha = 1;
    }

    if (onboardingStep === MainOnboardingSteps.Taps) {
      this.emitter.startEmit();

      this.spaceship.spaceshipMessageComponent.setTooltipId(MainOnboardingSteps.Taps);
      this.placeTooltipAroundEmitter();
    }

    if (onboardingStep === MainOnboardingSteps.ContinueTap) {
      this.spaceship.spaceshipMessageComponent.setTooltipId(MainOnboardingSteps.ContinueTap);

      this.placeTooltipAboveSpaceship();
    }

    if (onboardingStep === MainOnboardingFinishSteps.FinishWithoutGift) {
      this.spaceship.spaceshipMessageComponent.setTooltipId(MainOnboardingFinishSteps.FinishWithoutGift);

      this.placeTooltipAboveSpaceship();

      this.gcs.time.setTimeout(() => {
        const onboardingStep = this.onboardingStore.getOnboardingStep();

        // если спустя 3 сек все еще висит подсказка, скрываем ее
        if (onboardingStep === MainOnboardingFinishSteps.FinishWithoutGift) {
          this.onboardingService.nextMainOnboardingStep();
        }
      }, TOOLTIP_VISIBLE_TIME);
    }

    if (onboardingStep === TooltipId.KotletkaMessage) {
      this.spaceship.spaceshipMessageComponent.setTooltipId(TooltipId.KotletkaMessage);
      this.placeTooltipAboveSpaceship();
    }

    if (onboardingStep === TooltipId.SoonRecalculation) {
      this.spaceship.spaceshipMessageComponent.setTooltipId(TooltipId.SoonRecalculation);
      this.placeTooltipAboveSpaceship();
    }

    if (onboardingStep === MainOnboardingSteps.LeaderboardLocked) {
      this.tapsCounter = 0;
    }
  };

  private hideTapsTip = (): void => {
    this.onboardingService.nextMainOnboardingStep();

    this.emitter.stopEmit();
    this.emitter.clean();
  };

  private placeTooltipAroundEmitter(): void {
    const spawnPos = this.emitter.config.spawnPosition || new Point();
    this.spaceship?.spaceshipMessageComponent.placeTooltip(spawnPos.x, spawnPos.y);
  }

  private placeTooltipAboveSpaceship(): void {
    if (!this.spaceship) return;

    const {x, y} = this.spaceship.transformComponent;
    const height = this.spaceship.viewComponent.height;

    this.spaceship.spaceshipMessageComponent.placeTooltip(x, y - height / 2 + 20);
  }

  private createEmitter = (container: Container): ParticleEmitter => {
    const emitter = new ParticleEmitter<ContainerChild>(container, this.createSprite, {
      emitterConfig: {spawnInterval: 250, autoStart: false},
      particleConfig: waveConfig(),
    });
    return emitter;
  };

  private createSprite = (): Sprite => {
    const sprite = new Sprite(this.assetsLoader.bundle.wave);
    sprite.anchor.set(0.5);
    return sprite;
  };
}

injected(
  OnboardingScript,
  DI_TOKENS.gcsEngine,
  DI_TOKENS.clickerGameAssetsLoader,
  DI_TOKENS.onboardingStore,
  DI_TOKENS.onboardingService,
  DI_TOKENS.callbackCollector,
  DI_TOKENS.clickerGameEvents,
);
